import {
  type MutationMeta,
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useEffectEvent, useState, useSyncExternalStore } from 'react';

import { FIRESTORE_ENABLED } from '@/config/env';
import { apiClient } from '@/lib/apiClient';
import {
  type CollectionQuery,
  type Filter,
  type FirestoreError,
  type StoredDocument,
  watchCollection,
  watchDocument,
} from '@/lib/firebase';
import {
  type LiveRow,
  markPendingWrite,
  NO_ROWS,
  observeRows,
  type PendingKind,
  scopeOf,
  snapshot,
  subscribe,
  unmarked,
} from '@/lib/pendingWrites';
import { dropFromList, echoIntoList } from '@/lib/queryClient';
import { parseItems } from '@/lib/readers';

export interface StreamConfig {
  onPermissionDenied?: () => void;
}

let config: StreamConfig = {};

/** Sets the handler that runs when the rules deny a read. */
export function configureFirestoreStream(next: StreamConfig) {
  config = next;
}

/** Calls the denial handler when the error is `permission-denied`. */
export function reportStreamError(error: FirestoreError): void {
  if (error.code === 'permission-denied') config.onPermissionDenied?.();
}

export interface StreamState<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
}

const IDLE_STREAM: StreamState<never> = { data: [], loading: false, error: null };

export interface StreamOptions<T> {
  path: string;
  parse: (id: string, raw: Record<string, unknown>) => T | null;
  limit: number;
  orderByField?: string;
  direction?: 'asc' | 'desc';
  filters?: readonly Filter[];
  enabled?: boolean;
}

/** What one shared listener knows, before any hook has parsed it. */
interface SharedState {
  rows: StoredDocument[];
  loading: boolean;
  error: Error | null;
}

type SharedListener = (state: SharedState) => void;

interface SharedSubscription {
  state: SharedState;
  listeners: Set<SharedListener>;
  stop?: () => void;
  cancelled: boolean;
}

type Watch = (onRows: (rows: StoredDocument[]) => void, onError: (error: FirestoreError) => void) => Promise<() => void>;

/** One listener for each different query or document. All the hooks with that key use it. */
const subscriptions = new Map<string, SharedSubscription>();

function publish(subscription: SharedSubscription, state: SharedState) {
  subscription.state = state;
  for (const listener of [...subscription.listeners]) listener(state);
}

/** Opens the watch once per key, and gives every later hook the state the first one has. */
function attach(key: string, watch: Watch, reportsDenial: boolean, listener: SharedListener): () => void {
  let subscription = subscriptions.get(key);
  if (!subscription) {
    const created: SharedSubscription = {
      state: { rows: [], loading: true, error: null },
      listeners: new Set(),
      cancelled: false,
    };
    subscriptions.set(key, created);
    subscription = created;
    // Removes the failed watch from the map and sends the error to its listeners.
    const fail = (error: Error) => {
      if (subscriptions.get(key) === created) subscriptions.delete(key);
      publish(created, { rows: [], loading: false, error });
    };
    watch(
      (rows) => publish(created, { rows, loading: false, error: null }),
      (error) => {
        if (reportsDenial) reportStreamError(error);
        fail(error);
      },
    )
      .then((stop) => (created.cancelled ? stop() : void (created.stop = stop)))
      .catch(fail);
  }

  const shared = subscription;
  shared.listeners.add(listener);
  listener(shared.state);

  return () => {
    shared.listeners.delete(listener);
    if (shared.listeners.size > 0) return;
    shared.cancelled = true;
    shared.stop?.();
    if (subscriptions.get(key) === shared) subscriptions.delete(key);
  };
}

function parseRows<T>(rows: StoredDocument[], parse: StreamOptions<T>['parse']): T[] {
  return rows.map((row) => parse(row.id, row.raw)).filter((row): row is T => row !== null);
}

/** Returns the live rows of one collection query, shared with every hook that has the same query. */
export function useFirestoreStream<T>({
  path,
  parse,
  limit,
  orderByField,
  direction = 'desc',
  filters,
  enabled = true,
}: StreamOptions<T>): StreamState<T> {
  const active = enabled && FIRESTORE_ENABLED;
  const key = JSON.stringify({ path, limit, orderByField, direction, filters: filters ?? [] });

  const [state, setState] = useState<StreamState<T> & { key: string }>({
    data: [],
    loading: active,
    error: null,
    key,
  });

  const parseRow = useEffectEvent((id: string, raw: Record<string, unknown>) => parse(id, raw));

  useEffect(() => {
    if (!active) return;

    const spec = JSON.parse(key) as CollectionQuery;
    return attach(
      key,
      (onRows, onError) => watchCollection(spec, onRows, onError),
      true,
      (shared) =>
        setState({
          data: parseRows(shared.rows, parseRow),
          loading: shared.loading,
          error: shared.error,
          key,
        }),
    );
  }, [active, key]);

  if (!active) return IDLE_STREAM;
  const { key: loadedKey, ...current } = state;
  return loadedKey === key ? current : { data: [], loading: true, error: null };
}

export interface DocumentState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

const IDLE: DocumentState<never> = { data: null, loading: false, error: null };

export interface DocumentOptions<T> {
  path: string;
  parse: (raw: Record<string, unknown>) => T | null;
  enabled?: boolean;
  endsSessionOnDenial?: boolean;
}

/** Returns one live document, shared with every hook that reads the same path. */
export function useFirestoreDocument<T>({
  path,
  parse,
  enabled = true,
  endsSessionOnDenial = true,
}: DocumentOptions<T>): DocumentState<T> {
  const active = enabled && FIRESTORE_ENABLED;

  const [state, setState] = useState<DocumentState<T> & { path: string }>({
    data: null,
    loading: active,
    error: null,
    path,
  });
  const parseDocument = useEffectEvent((raw: Record<string, unknown>) => parse(raw));

  useEffect(() => {
    if (!active) return;

    return attach(
      JSON.stringify({ document: path, endsSessionOnDenial }),
      (onRows, onError) => watchDocument(path, onRows, onError),
      endsSessionOnDenial,
      ({ rows: [row], loading, error }) =>
        setState({ data: row ? parseDocument(row.raw) : null, loading, error, path }),
    );
  }, [active, path, endsSessionOnDenial]);

  if (!active) return IDLE;
  const { path: loadedPath, ...current } = state;
  return loadedPath === path ? current : { data: current.data, loading: true, error: null };
}

/**
 * Puts the rows this device wrote into the live rows, each marked with its write.
 * A list that is still loading confirms nothing.
 */
export function usePendingWrites<T extends LiveRow>(
  scope: string,
  rows: readonly T[],
  loading: boolean,
): T[] {
  const marks = useSyncExternalStore(subscribe, snapshot, snapshot);

  useEffect(() => {
    if (!loading) observeRows(scope, rows);
  }, [scope, rows, loading]);

  const mine = new Map(
    marks.filter((entry) => entry.scope === scope).map((entry) => [entry.id, entry]),
  );
  if (mine.size === 0) return rows as T[];

  const marked = rows.map((row) => {
    const entry = mine.get(row.id);
    return entry ? { ...row, pendingWrite: entry.kind } : row;
  });

  const known = new Set(rows.map((row) => row.id));
  const added = [...mine.values()]
    .filter((entry) => entry.kind === 'create' && entry.row && !known.has(entry.id))
    .map((entry) => ({ ...(entry.row as T), pendingWrite: entry.kind }));

  return [...added, ...marked];
}

/** Puts the writes of this device into the items of one array on a document.
 *  `idOf` must be a stable function. */
export function usePendingItems<T extends object>(
  scope: string,
  items: readonly T[],
  idOf: (item: T) => string,
): (T & LiveRow)[] {
  return usePendingWrites(scope, items.map((item) => ({ ...item, id: idOf(item) })), false);
}

export interface LiveCollectionOptions<T> {
  path: string;
  httpPath: string;
  queryKey: readonly unknown[];
  parse: (id: string, raw: Record<string, unknown>) => T | null;
  limit: number;
  orderByField?: string;
  direction?: 'asc' | 'desc';
  filters?: readonly Filter[];
  enabled?: boolean;
  /** The name the writes of this list use. It defaults to the query key. */
  scope?: readonly unknown[];
}

export interface LiveCollection<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/** Returns a live list: the stream with Firestore, the HTTP list without it, and this device's pending writes. */
export function useLiveCollection<T extends LiveRow>({
  path,
  httpPath,
  queryKey,
  parse,
  limit,
  orderByField,
  direction,
  filters,
  enabled = true,
  scope,
}: LiveCollectionOptions<T>): LiveCollection<T> {
  const stream = useFirestoreStream<T>({
    path,
    parse,
    limit,
    orderByField,
    direction,
    filters,
    enabled: enabled && FIRESTORE_ENABLED,
  });

  const query = useQuery({
    queryKey,
    enabled: enabled && !FIRESTORE_ENABLED,
    queryFn: async () => parseItems(await apiClient.get(httpPath), parse),
  });

  const live = FIRESTORE_ENABLED
    ? { rows: stream.data, loading: stream.loading, error: stream.error }
    : { rows: query.data ?? NO_ROWS, loading: query.isPending && enabled, error: query.error };

  const data = usePendingWrites<T>(scopeOf(scope ?? queryKey), live.rows, live.loading);

  return {
    data,
    loading: live.loading,
    error: live.error,
    refetch: () => {
      if (!FIRESTORE_ENABLED) void query.refetch();
    },
  };
}

/** A value given up front, or read from the response. */
export type FromWrite<TVariables, TBody, TValue> =
  | TValue
  | ((body: TBody, variables: TVariables) => TValue);

export interface LiveMutationOptions<TVariables, TBody, T extends LiveRow> {
  /** The list this write changes. */
  queryKey: FromWrite<TVariables, TBody, readonly unknown[]>;
  kind: FromWrite<TVariables, TBody, PendingKind>;
  mutationFn: (variables: TVariables) => Promise<TBody>;
  /** The stored row the response describes, as the list holds it. */
  rowOf: (body: TBody, variables: TVariables) => T | null;
  /** The name the list gives `useLiveCollection`, when it differs from the query key. */
  scope?: FromWrite<TVariables, TBody, readonly unknown[]>;
  onSuccess?: (body: TBody, variables: TVariables) => void;
  onError?: (error: Error, variables: TVariables) => void;
  meta?: MutationMeta;
}

function resolve<TVariables, TBody, TValue>(
  value: FromWrite<TVariables, TBody, TValue>,
  body: TBody,
  variables: TVariables,
): TValue {
  return typeof value === 'function'
    ? (value as (body: TBody, variables: TVariables) => TValue)(body, variables)
    : value;
}

/**
 * Sends one write, puts its row in the list at once and marks that row until the
 * stored data shows it.
 */
export function useLiveMutation<TVariables, TBody, T extends LiveRow>({
  queryKey,
  kind,
  mutationFn,
  rowOf,
  scope,
  onSuccess,
  onError,
  meta,
}: LiveMutationOptions<TVariables, TBody, T>): UseMutationResult<TBody, Error, TVariables> {
  const client = useQueryClient();

  return useMutation({
    mutationFn,
    meta,
    onError,
    onSuccess: (body, variables) => {
      const written = rowOf(body, variables);
      if (written) {
        const row = unmarked(written);
        const list = resolve(queryKey, body, variables);
        const change = resolve(kind, body, variables);

        if (change === 'delete') dropFromList(client, list, row.id);
        else echoIntoList(client, list, row);

        markPendingWrite({
          scope: scopeOf(resolve(scope ?? queryKey, body, variables)),
          id: row.id,
          kind: change,
          row: change === 'delete' ? null : row,
        });
      }
      onSuccess?.(body, variables);
    },
  });
}
